// =====================================================
// MUI THEME BRIDGE — Connects design tokens to MUI ThemeProvider
// Provides palette + component-level style overrides
// =====================================================

import { brand, status, neutral, surface, text, radius, shadows } from './designTokens';

/**
 * Returns a theme overrides object containing:
 * - palette: primary, error, success, warning, info, text, background, divider
 * - components: MuiButton, MuiIconButton, MuiOutlinedInput, MuiInputLabel,
 *               MuiCard, MuiPaper, MuiTableCell, MuiTableRow, MuiChip,
 *               MuiTooltip, MuiMenu, MuiDialog, MuiDivider
 */
export function getBrandThemeOverrides() {
  return {
    // ─── PALETTE ──────────────────────────────────────
    palette: {
      primary: {
        main:          brand.main,
        dark:          brand.dark,
        light:         brand.light,
        contrastText:  text.inverse,
      },
      error: {
        main:  status.error.main,
        dark:  status.error.dark,
        light: status.error.bg,
      },
      success: {
        main:  status.success.main,
        dark:  status.success.dark,
        light: status.success.bg,
      },
      warning: {
        main:  status.warning.main,
        dark:  status.warning.dark,
        light: status.warning.bg,
      },
      info: {
        main:  status.info.main,
        dark:  status.info.dark,
        light: status.info.bg,
      },
      text: {
        primary:   text.primary,
        secondary: text.secondary,
      },
      background: {
        default: surface.pageBg,
        paper:   surface.cardBg,
      },
      divider: neutral[200],
      grey: { ...neutral },
    },

    // ─── COMPONENT OVERRIDES ──────────────────────────
    components: {

      // ── MuiButton ───────────────────────────────────
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: radius.md,
          },
          contained: {
            backgroundColor: brand.main,
            color: text.inverse,
            boxShadow: shadows.sm,
            '&:hover': {
              backgroundColor: brand.dark,
              boxShadow: shadows.brand,
            },
          },
          outlined: {
            borderColor: neutral[200],
            color: neutral[700],
            '&:hover': {
              borderColor: brand.main,
              color: brand.main,
              backgroundColor: brand[50],
            },
          },
        },
      },

      // ── MuiIconButton ──────────────────────────────
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: radius.md,
            '&:hover': {
              backgroundColor: brand[50],
              color: brand.main,
            },
          },
        },
      },

      // ── MuiOutlinedInput ───────────────────────────
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            backgroundColor: '#FFFFFF',
            borderRadius: 4,
            transition: 'all 0.2s ease',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#e5e7eb',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#c7d2fe',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: brand.main,
              borderWidth: '1.5px',
            },
          },
        },
      },

      // ── MuiInputLabel ──────────────────────────────
      MuiInputLabel: {
        styleOverrides: {
          root: {
            fontSize: '0.78rem',
            color: '#6b7280',
            '&.Mui-focused': {
              color: brand.main,
            },
          },
        },
      },
      
      // ── MuiFormHelperText ──────────────────────────
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            fontSize: '0.72rem',
            marginLeft: '4px',
          },
        },
      },

      // ── MuiTextField (inherits MuiOutlinedInput + MuiInputLabel) ──

      // ── MuiSelect (inherits MuiOutlinedInput) ─────

      // ── MuiCard ────────────────────────────────────
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundColor: surface.cardBg,
            border: `1px solid ${surface.borderDefault}`,
            borderRadius: radius.xl,
            boxShadow: shadows.card,
          },
        },
      },

      // ── MuiPaper ───────────────────────────────────
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },

      // ── MuiTableCell ──────────────────────────────
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: `1px solid ${surface.borderLight}`,
          },
          head: {
            backgroundColor: neutral[50],
            color: neutral[700],
            fontWeight: 600,
            textTransform: 'none',
            fontSize: '0.8125rem',
          },
        },
      },

      // ── MuiTableRow ───────────────────────────────
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&:hover': {
              backgroundColor: neutral[100],
            },
          },
        },
      },

      // ── MuiChip ───────────────────────────────────
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: radius.sm,
            fontWeight: 600,
          },
        },
      },

      // ── MuiTooltip ────────────────────────────────
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: neutral[800],
            borderRadius: radius.sm,
            fontSize: '0.75rem',
          },
        },
      },

      // ── MuiMenu ───────────────────────────────────
      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: radius.md,
            border: `1px solid ${neutral[200]}`,
            boxShadow: shadows.lg,
          },
        },
      },

      // ── MuiDialog ─────────────────────────────────
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: radius.lg,
          },
        },
      },

      // ── MuiDivider ────────────────────────────────
      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: neutral[200],
          },
        },
      },
    },
  };
}
